import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { useEffect, useState, useRef } from "react"; // Importa useRef
import fetchClientes from "../apis/clientes-api";
import {
  Box,
  Input,
  Text,
  Spinner,
  Button,
  Center,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  TableContainer,
  Select,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";
import { FaSearch } from "react-icons/fa";
import UserDataHome from "../components/user-data-home";
import Footer from "../components/footer";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa"; // Importa ícones de ordenação

const AtendimentoAvulso = () => {
  const {
    username,
    password,
    token,
    globalToken,
    rowItem,
    setRowItem,
    clienteCodigo,
    setClienteCodigo,
    setCodClienteGlobal,
    codClienteGlobal,
  } = useAppContext();
  const navigate = useNavigate();
  const toast = useToast();
  const [allClientes, setAllClientes] = useState([]); // Todos os clientes carregados
  const [displayedItems, setDisplayedItems] = useState(50); // Quantidade de itens a exibir
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("cliente"); // 'cliente', 'municipio', 'itinerario'
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [remainingRecords, setRemainingRecords] = useState(0);
  const [descriptionFilter, setDescriptionFilter] = useState("");
  const [nomeFilter, setNomeFilter] = useState("");
  const [municipioFilter, setMunicipioFilter] = useState("");

  const [sortDirectionCodigo, setSortDirectionCodigo] = useState(null);
  const [sortDirectionNome, setSortDirectionNome] = useState(null);
  const [sortDirectionUltCompra, setSortDirectionUltCompra] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const cancelRef = useRef();
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [licenseErrorModalOpen, setLicenseErrorModalOpen] = useState(false);
  const [licenseErrorMessage, setLicenseErrorMessage] = useState("");
  const observerTarget = useRef(null); // Referência para o elemento sentinela

  const [itinerarioFilter, setItinerarioFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 1000; // Quantidade de itens por página da API
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    // Verifica se username ou password estão vazios e redireciona
    if (username === "" || password === "") {
      navigate("/error");
    }

    // Recupera dados salvos no localStorage
    const savedData = localStorage.getItem("searchData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setAllClientes(parsedData); // Preenche com todos os dados
      setIsLoading(false); // Desativa o loading se já houver dados salvos
    }
  }, [username, navigate, password]);

  // Intersection Observer para lazy loading
  useEffect(() => {
    const loadMore = () => {
      if (isLoadingMore || isLoading) return;

      // Primeiro tenta mostrar mais itens já carregados
      if (displayedItems < allClientes.length) {
        // Ainda há itens carregados para mostrar
        setDisplayedItems((prev) => Math.min(prev + 50, allClientes.length));
      } else if (hasNext) {
        // Não há mais itens carregados, precisa buscar mais
        loadMoreItems();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNext, isLoadingMore, isLoading, displayedItems, allClientes.length]);

  // Função para carregar mais itens da API
  const loadMoreItems = async () => {
    if (isLoadingMore || !hasNext) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const params = {
        page: nextPage,
        token: globalToken.access_token,
        username,
        password,
      };

      if (searchType === "cliente") {
        params.search = searchValue;
        params.search_mun = "";
        params.search_ite = "";
      } else if (searchType === "municipio") {
        params.search = "";
        params.search_mun = searchValue;
        params.search_ite = "";
      } else if (searchType === "itinerario") {
        params.search = "";
        params.search_mun = "";
        params.search_ite = searchValue;
      }

      const data = await fetchClientes(params);

      if (data.items && data.items.length > 0) {
        setAllClientes((prev) => [...prev, ...data.items]);
        setHasNext(data.hasNext);
        setRemainingRecords(data.remainingRecords);
        setCurrentPage(nextPage);
        // Mostra mais 50 itens dos recém carregados
        setDisplayedItems((prev) => prev + Math.min(50, data.items.length));
      } else {
        setHasNext(false);
      }
    } catch (error) {
      console.error("Erro ao carregar mais itens:", error);
      if (error.response && error.response.status === 503) {
        const msg =
          error.response?.data?.message ||
          "Não existe licença disponível no License Server para atender a requisição nesse momento.";
        setLicenseErrorMessage(msg);
        setLicenseErrorModalOpen(true);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (username === "" || password === "") {
    navigate("/error");
  }

  // Função unificada de busca
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        token: globalToken.access_token,
        username,
        password,
      };

      // Adiciona o parâmetro correto baseado no tipo de busca
      if (searchType === "cliente") {
        params.search = searchValue;
        params.search_mun = "";
        params.search_ite = "";
      } else if (searchType === "municipio") {
        params.search = "";
        params.search_mun = searchValue;
        params.search_ite = "";
      } else if (searchType === "itinerario") {
        params.search = "";
        params.search_mun = "";
        params.search_ite = searchValue;
      }

      let data = await fetchClientes(params);

      if (data.items) {
        setAllClientes(data.items);
        setHasNext(data.hasNext);
        setRemainingRecords(data.remainingRecords);
        setTotalRecords(data.total || data.items.length);
        setDisplayedItems(Math.min(50, data.items.length)); // Mostra até 50 itens inicialmente
        setCurrentPage(1);

        localStorage.setItem("searchData", JSON.stringify(data.items));
      } else {
        setAllClientes([]);
        setHasNext(false);
        setRemainingRecords(0);
        setDisplayedItems(0);
      }
    } catch (error) {
      console.error("Erro ao buscar clientes", error);

      if (error.response && error.response.status === 503) {
        const msg =
          error.response?.data?.message ||
          "Não existe licença disponível no License Server para atender a requisição nesse momento.";
        setLicenseErrorMessage(msg);
        setLicenseErrorModalOpen(true);
        return;
      }

      if (!error.sessionExpired && error.userMessage) {
        toast({
          title: "Erro ao buscar clientes",
          description: error.userMessage,
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  console.log("cliente codigo", clienteCodigo);

  const handlePesquisar = (event) => {
    event.preventDefault();
    if (!searchValue.trim()) return;
    setDescriptionFilter("");
    setNomeFilter("");
    setMunicipioFilter("");
    setItinerarioFilter("");
    fetchData();
  };

  const handleEnterPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handlePesquisar(event);
    }
  };

  const getPlaceholder = () => {
    switch (searchType) {
      case "cliente":
        return "Digite o nome do cliente";
      case "municipio":
        return "Digite o nome do município";
      case "itinerario":
        return "Digite o itinerário";
      default:
        return "Digite o nome do cliente";
    }
  };

  const handleRowClick = (cliente) => {
    setSelectedCliente(cliente);
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    if (selectedCliente !== null) {
      setRowItem(selectedCliente);
      setCodClienteGlobal(selectedCliente.codgrupovenda);
      console.log("Cliente selecionado:", selectedCliente.codgrupovenda);
      setClienteCodigo(selectedCliente.codigo);
      console.log("Cliente selecionado: codigo", clienteCodigo);
      const clienteIndex = allClientes.indexOf(selectedCliente);
    }

    sessionStorage.setItem("codcg", codClienteGlobal);

    navigate(`/atendimento-1`); // Navega para a rota dinâmica
    setIsModalOpen(false);
  };

  // Filtrar e limitar itens exibidos
  const filteredResults = allClientes
    .filter(
      (item) =>
        item.codigo.toLowerCase().includes(descriptionFilter.toLowerCase()) &&
        item.nome.toLowerCase().includes(nomeFilter.toLowerCase()) &&
        item.municipio.toLowerCase().includes(municipioFilter.toLowerCase()) &&
        item.itinerario.toLowerCase().includes(itinerarioFilter.toLowerCase())
    )
    .slice(0, displayedItems); // Limita aos itens visíveis

  const sortClientes = (field, direction) => {
    const sortedClientes = [...allClientes].sort((a, b) => {
      if (field === "ult_compra") {
        // Tratar datas vazias como a data mais antiga ou mais recente
        const dateA = a.ult_compra
          ? new Date(a.ult_compra)
          : direction === "asc"
          ? new Date(8640000000000000)
          : new Date(-8640000000000000);
        const dateB = b.ult_compra
          ? new Date(b.ult_compra)
          : direction === "asc"
          ? new Date(8640000000000000)
          : new Date(-8640000000000000);

        if (dateA < dateB) return direction === "asc" ? -1 : 1;
        if (dateA > dateB) return direction === "asc" ? 1 : -1;
        return 0;
      } else {
        if (a[field] < b[field]) return direction === "asc" ? -1 : 1;
        if (a[field] > b[field]) return direction === "asc" ? 1 : -1;
        return 0;
      }
    });
    setAllClientes(sortedClientes);
  };

  const handleSort = (field) => {
    let newDirection;
    if (field === "codigo") {
      newDirection = sortDirectionCodigo === "asc" ? "desc" : "asc";
      setSortDirectionCodigo(newDirection);
      setSortDirectionNome(null); // Reseta a outra ordenação
      setSortDirectionUltCompra(null); // Reseta a ordenação de ult_compra
    } else if (field === "nome") {
      newDirection = sortDirectionNome === "asc" ? "desc" : "asc";
      setSortDirectionNome(newDirection);
      setSortDirectionCodigo(null); // Reseta a outra ordenação
      setSortDirectionUltCompra(null); // Reseta a ordenação de ult_compra
    } else if (field === "ult_compra") {
      newDirection = sortDirectionUltCompra === "asc" ? "desc" : "asc";
      setSortDirectionUltCompra(newDirection);
      setSortDirectionCodigo(null); // Reseta a ordenação de codigo
      setSortDirectionNome(null); // Reseta a ordenação de nome
    }
    sortClientes(field, newDirection);
  };

  function formatarData(data) {
    if (!data) return ""; // Retorna vazio se a data for inválida

    // Se a data já está no formato ISO (YYYY-MM-DD), parsear diretamente
    if (typeof data === "string" && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    }

    // Caso contrário, tentar parsear como Date
    try {
      const dataFormatada = new Date(data);

      // Verificar se a data é válida
      if (isNaN(dataFormatada.getTime())) {
        return data; // Retorna o valor original se não for uma data válida
      }

      const dia = String(dataFormatada.getDate()).padStart(2, "0");
      const mes = String(dataFormatada.getMonth() + 1).padStart(2, "0");
      const ano = dataFormatada.getFullYear();

      return `${dia}/${mes}/${ano}`;
    } catch (error) {
      return data; // Retorna o valor original em caso de erro
    }
  }

  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;

    const parts = text.split(new RegExp(`(${highlight})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} style={{ color: "red", fontWeight: "bold" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  return (
    <Flex direction="column" minHeight="100vh" bg="gray.50">
      <UserDataHome />
      <Box flex="1" bg="gray.50">
        <Box mt={8} px={{ base: 4, md: 8 }} maxW="1400px" mx="auto">
          {/* Formulário unificado de busca */}
          <Card
            bg="white"
            borderRadius="xl"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            mb={6}
          >
            <CardBody p={6}>
              <form onSubmit={handlePesquisar}>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  gap={4}
                  align={{ base: "stretch", md: "flex-end" }}
                >
                  <Box flex={{ base: "1", md: "0 0 200px" }}>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color="#c0a316"
                      mb={2}
                    >
                      Tipo de Busca
                    </Text>
                    <Select
                      value={searchType}
                      onChange={(e) => {
                        setSearchType(e.target.value);
                        setSearchValue("");
                      }}
                      bg="gray.50"
                      borderColor="gray.300"
                      _focus={{
                        borderColor: "#007abe",
                        boxShadow: "0 0 0 1px #007abe",
                      }}
                      size="md"
                    >
                      <option value="cliente">Cliente</option>
                      <option value="municipio">Município</option>
                      <option value="itinerario">Itinerário</option>
                    </Select>
                  </Box>

                  <Box flex="1">
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color="#c0a316"
                      mb={2}
                    >
                      {searchType === "cliente"
                        ? "Nome do Cliente"
                        : searchType === "municipio"
                        ? "Nome do Município"
                        : "Itinerário"}
                    </Text>
                    <Input
                      placeholder={getPlaceholder()}
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      onKeyDown={handleEnterPress}
                      bg="gray.50"
                      borderColor="gray.300"
                      _focus={{
                        borderColor: "#007abe",
                        boxShadow: "0 0 0 1px #007abe",
                      }}
                      size="md"
                    />
                  </Box>

                  <Button
                    type="submit"
                    leftIcon={<FaSearch />}
                    bg="#007abe"
                    color="white"
                    _hover={{
                      bg: "#0066a3",
                      transform: "translateY(-2px)",
                      boxShadow: "md",
                    }}
                    transition="all 0.2s"
                    size="md"
                    px={8}
                    isLoading={isLoading}
                    loadingText="Buscando..."
                    isDisabled={!searchValue.trim()}
                  >
                    Buscar
                  </Button>
                </Flex>
              </form>
            </CardBody>
          </Card>
          {/* {hasNext !== undefined && remainingRecords !== undefined && (
            <Text mt={2}>
              {hasNext
                ? `Mais resultados disponíveis (${remainingRecords} restantes)`
                : ""}
            </Text>
          )}
          <HStack mt={5}>
            <Button
              isDisabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              colorScheme="teal"
            >
              Página Anterior
            </Button>

            <Text fontWeight="bold">Página {currentPage}</Text>

            <Button
              isDisabled={!hasNext}
              onClick={() => handlePageChange(currentPage + 1)}
              colorScheme="teal"
            >
              Próxima Página
            </Button>
          </HStack> */}
        </Box>
        <Box mt={4}>
          {isLoading ? (
            <Center mt="15%">
              <Spinner size="xl" color="#1A202C" />
            </Center>
          ) : (
            <Box>
              {allClientes.length > 0 ? (
                <TableContainer
                  maxHeight="99vh"
                  overflowY="auto"
                  sx={{
                    "&::-webkit-scrollbar": {
                      width: "8px",
                      height: "8px",
                      backgroundColor: "white",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#555",
                    },
                  }}
                >
                  <Table
                    bg="white"
                    size="sm"
                    borderRadius="lg"
                    overflow="hidden"
                  >
                    {/* Cabeçalho fixo */}
                    <Thead
                      bg="#007abe"
                      position="sticky"
                      top="0"
                      zIndex="100"
                      boxShadow="md"
                    >
                      <Tr>
                        <Th
                          color="white"
                          verticalAlign="top"
                          whiteSpace="nowrap"
                          maxW="150px"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Flex direction="column" gap={2}>
                            <Flex align="center" justify="space-between">
                              <Text
                                fontSize="xs"
                                fontWeight="bold"
                                textTransform="uppercase"
                                letterSpacing="wide"
                              >
                                Código
                              </Text>
                              <Button
                                size="xs"
                                onClick={() => handleSort("codigo")}
                                variant="ghost"
                                color="white"
                                _hover={{
                                  bg: "rgba(255,255,255,0.1)",
                                  color: "#c0a316",
                                }}
                                minW="auto"
                                h="auto"
                                p={1}
                              >
                                {sortDirectionCodigo === "asc" ? (
                                  <FaSortUp size="12px" />
                                ) : sortDirectionCodigo === "desc" ? (
                                  <FaSortDown size="12px" />
                                ) : (
                                  <FaSort size="12px" />
                                )}
                              </Button>
                            </Flex>
                            <Input
                              bg="white"
                              borderColor="gray.300"
                              _focus={{
                                borderColor: "#c0a316",
                                boxShadow: "0 0 0 1px #c0a316",
                              }}
                              _placeholder={{ color: "gray.400" }}
                              placeholder="Filtrar"
                              size="sm"
                              value={descriptionFilter}
                              onChange={(e) =>
                                setDescriptionFilter(e.target.value)
                              }
                              borderRadius="md"
                            />
                          </Flex>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          whiteSpace="nowrap"
                          maxW="100px"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            Loja
                          </Text>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          maxW="250px"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Flex direction="column" gap={2}>
                            <Flex align="center" justify="space-between">
                              <Text
                                fontSize="xs"
                                fontWeight="bold"
                                textTransform="uppercase"
                                letterSpacing="wide"
                              >
                                Nome
                              </Text>
                              <Button
                                size="xs"
                                onClick={() => handleSort("nome")}
                                variant="ghost"
                                color="white"
                                _hover={{
                                  bg: "rgba(255,255,255,0.1)",
                                  color: "#c0a316",
                                }}
                                minW="auto"
                                h="auto"
                                p={1}
                              >
                                {sortDirectionNome === "asc" ? (
                                  <FaSortUp size="12px" />
                                ) : sortDirectionNome === "desc" ? (
                                  <FaSortDown size="12px" />
                                ) : (
                                  <FaSort size="12px" />
                                )}
                              </Button>
                            </Flex>
                            <Input
                              bg="white"
                              borderColor="gray.300"
                              _focus={{
                                borderColor: "#c0a316",
                                boxShadow: "0 0 0 1px #c0a316",
                              }}
                              _placeholder={{ color: "gray.400" }}
                              placeholder="Filtrar"
                              size="sm"
                              value={nomeFilter}
                              onChange={(e) => setNomeFilter(e.target.value)}
                              borderRadius="md"
                            />
                          </Flex>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          maxW="300px"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            Nome Fantasia
                          </Text>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          maxW="300px"
                          whiteSpace="normal"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            Endereço
                          </Text>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          maxW="100px"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            Estado
                          </Text>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          maxW="150px"
                          whiteSpace="normal"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Flex direction="column" gap={2}>
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              textTransform="uppercase"
                              letterSpacing="wide"
                            >
                              Município
                            </Text>
                            <Input
                              bg="white"
                              borderColor="gray.300"
                              _focus={{
                                borderColor: "#c0a316",
                                boxShadow: "0 0 0 1px #c0a316",
                              }}
                              _placeholder={{ color: "gray.400" }}
                              placeholder="Filtrar"
                              size="sm"
                              value={municipioFilter}
                              onChange={(e) =>
                                setMunicipioFilter(e.target.value)
                              }
                              borderRadius="md"
                            />
                          </Flex>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          maxW="150px"
                          whiteSpace="nowrap"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Flex align="center" justify="space-between">
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              textTransform="uppercase"
                              letterSpacing="wide"
                            >
                              Últ. Compra
                            </Text>
                            <Button
                              size="xs"
                              onClick={() => handleSort("ult_compra")}
                              variant="ghost"
                              color="white"
                              _hover={{
                                bg: "rgba(255,255,255,0.1)",
                                color: "#c0a316",
                              }}
                              minW="auto"
                              h="auto"
                              p={1}
                            >
                              {sortDirectionUltCompra === "asc" ? (
                                <FaSortUp size="12px" />
                              ) : sortDirectionUltCompra === "desc" ? (
                                <FaSortDown size="12px" />
                              ) : (
                                <FaSort size="12px" />
                              )}
                            </Button>
                          </Flex>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          maxW="150px"
                          whiteSpace="nowrap"
                          px={4}
                          py={4}
                          borderRight="1px solid"
                          borderColor="rgba(255,255,255,0.2)"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            Cód. Vendedor
                          </Text>
                        </Th>

                        <Th
                          color="white"
                          verticalAlign="top"
                          maxW="300px"
                          whiteSpace="normal"
                          px={4}
                          py={4}
                        >
                          <Flex direction="column" gap={2}>
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              textTransform="uppercase"
                              letterSpacing="wide"
                            >
                              Itinerário
                            </Text>
                            <Input
                              bg="white"
                              borderColor="gray.300"
                              _focus={{
                                borderColor: "#c0a316",
                                boxShadow: "0 0 0 1px #c0a316",
                              }}
                              _placeholder={{ color: "gray.400" }}
                              placeholder="Filtrar"
                              size="sm"
                              value={itinerarioFilter}
                              onChange={(e) =>
                                setItinerarioFilter(e.target.value)
                              }
                              borderRadius="md"
                            />
                          </Flex>
                        </Th>
                      </Tr>
                    </Thead>

                    {/* Corpo da Tabela */}
                    <Tbody>
                      {filteredResults.length > 0 ? (
                        filteredResults.map((cliente, index) => (
                          <Tr
                            key={index}
                            onClick={() => handleRowClick(cliente)}
                            style={{ cursor: "pointer" }}
                            _hover={{
                              bg: "blue.50",
                              transform: "scale(1.01)",
                              boxShadow: "sm",
                            }}
                            transition="all 0.2s"
                          >
                            <Td maxW="150px" whiteSpace="normal">
                              {cliente.codigo}
                            </Td>
                            <Td maxW="100px" whiteSpace="normal">
                              {cliente["loja  "]}
                            </Td>
                            <Td maxW="250px" whiteSpace="normal">
                              {cliente.nome}
                            </Td>
                            <Td maxW="300px" whiteSpace="normal">
                              {cliente.nomefantasia}
                            </Td>
                            <Td maxW="300px" whiteSpace="normal">
                              {cliente.endereco}
                            </Td>
                            <Td maxW="100px" whiteSpace="normal">
                              {cliente.estado}
                            </Td>
                            <Td maxW="150px" whiteSpace="normal">
                              {cliente.municipio}
                            </Td>
                            <Td maxW="150px" whiteSpace="nowrap">
                              {formatarData(cliente.ult_compra)}
                            </Td>
                            <Td maxW="150px" whiteSpace="nowrap">
                              {cliente.cod_vend}
                            </Td>
                            <Td maxW="300px" whiteSpace="normal">
                              {highlightText(
                                cliente.itinerario,
                                itinerarioFilter
                              )}
                            </Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan="10">
                            <Text align="center">
                              Nenhum cliente encontrado
                            </Text>
                          </Td>
                        </Tr>
                      )}
                      {/* Elemento sentinela para lazy loading */}
                      {(hasNext || displayedItems < allClientes.length) && (
                        <Tr ref={observerTarget}>
                          <Td colSpan="10" py={4}>
                            <Center>
                              {isLoadingMore ? (
                                <Flex align="center" gap={3}>
                                  <Spinner size="sm" color="#007abe" />
                                  <Text color="#007abe" fontSize="sm">
                                    Carregando mais itens...
                                  </Text>
                                </Flex>
                              ) : (
                                <Text color="gray.500" fontSize="sm">
                                  Role para carregar mais
                                </Text>
                              )}
                            </Center>
                          </Td>
                        </Tr>
                      )}
                      {/* Indicador de fim da lista */}
                      {!hasNext &&
                        displayedItems >= allClientes.length &&
                        allClientes.length > 0 && (
                          <Tr>
                            <Td colSpan="10" py={4}>
                              <Center>
                                <Text color="gray.500" fontSize="sm">
                                  Todos os {totalRecords} registros foram
                                  exibidos
                                </Text>
                              </Center>
                            </Td>
                          </Tr>
                        )}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <Center mt="20%">
                  <Text fontSize="xl">
                    Nenhum cliente com este nome encontrado.
                  </Text>
                </Center>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <AlertDialog
        isOpen={isModalOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsModalOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmação
            </AlertDialogHeader>

            <AlertDialogBody>
              Deseja iniciar um lançamento para este cliente?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsModalOpen(false)}>
                Não
              </Button>
              <Button colorScheme="blue" onClick={handleConfirm} ml={3}>
                Sim
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal
        isOpen={licenseErrorModalOpen}
        onClose={() => setLicenseErrorModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader color="red.600">
            Indisponibilidade do serviço
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{licenseErrorMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={() => setLicenseErrorModalOpen(false)}
            >
              Entendi
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Footer />
    </Flex>
  );
};

export default AtendimentoAvulso;
